import React, { useRef, useState } from "react";
import type { Theme } from "../lib/theme";
import * as Icon from "../components/OEIcons";
import { useEntitlements } from "../hooks/useEntitlements";
import { ALL_DIVISIONS } from "../projectData";
import { DEFAULT_LABOR_RATES, DEFAULT_ORG_EXCLUSIONS } from "../lib/orgDefaults";
import { DEFAULT_BEST_PRACTICES, CATEGORIES as BP_CATEGORIES, ALL_DIVS as BP_ALL_DIVS, BPModal } from "../components/LessonsLibrary";
import ActivityLogPane from "../components/ActivityLogPane";
import CostCodeLibrary from "../components/CostCodeLibrary";
import type { PhaseGroup } from "../lib/phaseGroups";
import { DEFAULT_PHASE_GROUPS } from "../lib/phaseGroups";
import type { ShowToast } from "../types";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface SettingsPanelProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  /** The tab this trip into Settings started from, if it started anywhere. */
  settingsFrom?: string | null;
  /** What that tab is called, for the label on the way back. */
  settingsFromLabel?: string;
  user: any;
  cloudStorage: any;
  cloudProjectId: string | null;
  orgPartners: any[];
  orgPartnerSearch: string;
  setOrgPartnerSearch: React.Dispatch<React.SetStateAction<string>>;
  orgs: any[];
  inviteEmails: Record<string, string>;
  setInviteEmails: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  inviteRoles: Record<string, string>;
  setInviteRoles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  newOrgName: string;
  setNewOrgName: React.Dispatch<React.SetStateAction<string>>;
  ownershipRequests: Record<string, any[]>;
  editDisplayName: string;
  setEditDisplayName: React.Dispatch<React.SetStateAction<string>>;
  profileMsg: string;
  setProfileMsg: React.Dispatch<React.SetStateAction<string>>;
  profileSaving: boolean;
  setProfileSaving: React.Dispatch<React.SetStateAction<boolean>>;
  orgExclusions: any[];
  setOrgExclusions: React.Dispatch<React.SetStateAction<any[]>>;
  laborRates: any[];
  setLaborRates: React.Dispatch<React.SetStateAction<any[]>>;
  bestPractices: any[];
  setBestPractices: React.Dispatch<React.SetStateAction<any[]>>;
  bpOrgEditItem: any;
  setBpOrgEditItem: React.Dispatch<React.SetStateAction<any>>;
  bpOrgDeleteConfirmId: string | null;
  setBpOrgDeleteConfirmId: React.Dispatch<React.SetStateAction<string | null>>;
  bpOrgFilterCat: string;
  setBpOrgFilterCat: React.Dispatch<React.SetStateAction<string>>;
  presets: any[];
  editingPresetId: string | null;
  setEditingPresetId: React.Dispatch<React.SetStateAction<string | null>>;
  newPresetName: string;
  setNewPresetName: React.Dispatch<React.SetStateAction<string>>;
  showNewPresetForm: boolean;
  setShowNewPresetForm: React.Dispatch<React.SetStateAction<boolean>>;
  procoreConnected: boolean;
  setProcoreConnected: React.Dispatch<React.SetStateAction<boolean>>;
  orgMsg: string;
  orgsLoading: boolean;
  orgLogos: Record<string, string>;
  setOrgLogos: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setOrgLogo: (orgId: string, logo: string) => void;
  clearOrgLogo: (orgId: string) => void;
  procoreConnecting: boolean;
  showToast: ShowToast;
  uid: () => string;
  createOrg: () => void;
  inviteMember: (orgId: string) => void;
  changeOrgRole: (orgId: string, userId: string, newRole: string) => void;
  requestOwnership: (orgId: string) => void;
  confirmApproveTransfer: (requestId: string, orgId: string, requesterId: string, requesterName: string) => void;
  respondToRequest: (requestId: string, orgId: string, requesterId: string, approve: boolean) => void;
  confirmRemoveMember: (orgId: string, orgName: string, memberName: string, memberId: string) => void;
  revokeInvite: (orgId: string, inviteId: string) => void;
  confirmDeleteOrg: (orgId: string, orgName: string) => void;
  addPreset: (name: string) => void;
  deletePreset: (id: string) => void;
  duplicatePreset: (p: any) => void;
  savePreset: (preset: any) => void;
  handleProcoreConnect: () => void;
  phaseGroups: PhaseGroup[];
  setPhaseGroups: React.Dispatch<React.SetStateAction<PhaseGroup[]>>;
  // Admin-only
  isAdmin?: boolean;
  accessRequests?: AccessRequest[];
  accessRequestsLoading?: boolean;
  onApproveRequest?: (email: string) => Promise<void>;
  onRejectRequest?: (email: string) => Promise<void>;
  onInviteUser?: (name: string, email: string) => Promise<void>;
}

export interface AccessRequest {
  id: string;
  name: string;
  email: string;
  message: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  resolved_at?: string | null;
}

export default function SettingsPanel({
  T, isMobile, isTablet,
  activeTab, setActiveTab, settingsFrom, settingsFromLabel,
  user, cloudStorage, cloudProjectId,
  orgPartners, orgPartnerSearch, setOrgPartnerSearch,
  orgs,
  inviteEmails, setInviteEmails,
  inviteRoles, setInviteRoles,
  newOrgName, setNewOrgName,
  ownershipRequests,
  editDisplayName, setEditDisplayName,
  profileMsg, setProfileMsg,
  profileSaving, setProfileSaving,
  orgExclusions, setOrgExclusions,
  laborRates, setLaborRates,
  bestPractices, setBestPractices,
  bpOrgEditItem, setBpOrgEditItem,
  bpOrgDeleteConfirmId, setBpOrgDeleteConfirmId,
  bpOrgFilterCat, setBpOrgFilterCat,
  presets, editingPresetId, setEditingPresetId,
  newPresetName, setNewPresetName,
  showNewPresetForm, setShowNewPresetForm,
  procoreConnected, setProcoreConnected,
  orgMsg, orgsLoading, orgLogos, setOrgLogos, setOrgLogo, clearOrgLogo,
  procoreConnecting,
  showToast, uid,
  createOrg, inviteMember,
  changeOrgRole, requestOwnership, confirmApproveTransfer, respondToRequest,
  confirmRemoveMember, revokeInvite, confirmDeleteOrg,
  addPreset, deletePreset, duplicatePreset, savePreset, handleProcoreConnect,
  phaseGroups, setPhaseGroups,
  isAdmin, accessRequests = [], accessRequestsLoading, onApproveRequest, onRejectRequest, onInviteUser,
}: SettingsPanelProps) {
  const entitlements = useEntitlements(user?.id);
  const [accessStatusTab, setAccessStatusTab] = React.useState<"pending"|"approved"|"rejected">("pending");
  const [accessActionInFlight, setAccessActionInFlight] = React.useState<string|null>(null);
  const [inviteName, setInviteName] = React.useState("");
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteSending, setInviteSending] = React.useState(false);
  const [pgEditId, setPgEditId] = useState<string | null>(null);
  const [pgDivOpen, setPgDivOpen] = useState<string | null>(null); // which group's division picker is open

/**
 * The way back to whatever sent you here.
 *
 * Settings is a destination with its own sidebar, so arriving from the
 * Schedule to change one thing left no trace of the work you were in the
 * middle of -- the only route back was to recognise the tab you had come
 * from among the others and click it.
 */
const backLink = settingsFrom ? (
  <button onClick={()=>setActiveTab(settingsFrom)}
    title={`Back to ${settingsFromLabel||"where you were"}`}
    style={{display:"inline-flex",alignItems:"center",gap:6,background:"none",
      border:`1px solid ${T.borderSecondary}`,borderRadius:3,color:T.textMuted,
      padding:"4px 10px",fontSize:11,fontFamily:FONT,cursor:"pointer",
      margin:"18px 0 -6px 28px"}}
    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}
    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.borderSecondary;e.currentTarget.style.color=T.textMuted;}}>
    <span style={{fontSize:13,lineHeight:1}}>&#8592;</span>
    <span>Back to {settingsFromLabel||"where you were"}</span>
  </button>
) : null;
return (
  <div className="fade-in" style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>

    {/* ── Sidebar nav (desktop) ── */}
    {!isMobile&&(
      <div style={{width:196,flexShrink:0,borderRight:`1px solid ${T.borderPrimary}`,background:T.bgPanel,overflowY:"auto",padding:"28px 0"}}>
        {([
          {group:"Account",items:[{id:"settings-profile",icon:"User",label:"Profile"}]},
          {group:"Team",items:[{id:"settings-team",icon:"Building",label:"Members & Roles"}]},
          {group:"Libraries",items:[
            {id:"settings-costcodes",icon:"Layers",label:"Cost Codes"},
            {id:"settings-exclusions",icon:"NoEntry",label:"Exclusions"},
            {id:"settings-labor",icon:"Hammer",label:"Labor Rates"},
            {id:"settings-lessons",icon:"Flag",label:"Lessons"},
          ]},
          {group:"Defaults",items:[
            {id:"settings-presets",icon:"Settings",label:"Presets"},
            {id:"settings-phase-groups",icon:"Layers",label:"Phases"},
          ]},
          {group:"Integrations",items:[{id:"settings-integrations",icon:"Plug",label:"Integrations"}]},
          // Activity Log hidden until the write side is instrumented.
          // ActivityLogPane component and settings-activity panel are
          // kept in code (~OpenEstimate.tsx line 7900) for later use.
          // {group:"Activity",items:[{id:"settings-activity",icon:"Clipboard",label:"Activity Log"}]},
          ...(isAdmin ? [{group:"Admin",items:[{id:"settings-access",icon:"Bell",label:"Beta Access",badge:(accessRequests||[]).filter(r=>r.status==="pending").length}]}] : []),
        ] as {group:string;items:{id:string;icon:string;label:string;badge?:number}[]}[]).map(({group,items})=>(
          <div key={group} style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase" as any,color:T.textWeak,padding:"0 20px 6px"}}>{group}</div>
            {items.map(item=>{
              const active=activeTab===item.id;
              return(
                <button key={item.id} onClick={()=>setActiveTab(item.id)}
                  style={{width:"100%",background:active?T.bgAccentFaint:"none",border:"none",borderLeft:`2px solid ${active?T.borderAccent:"transparent"}`,
                    color:active?T.textSecondary:T.textMuted,textAlign:"left" as any,padding:"8px 20px",fontSize:12,fontFamily:FONT,cursor:"pointer",
                    fontWeight:active?600:400,display:"flex",alignItems:"center",gap:9,transition:"all .12s"}}>
                  <span style={{fontSize:14,opacity:.75,flexShrink:0}}>{(()=>{const C=(Icon as any)[item.icon];return C?<C size={13}/>:null;})()}</span>
                  <span style={{flex:1}}>{item.label}</span>
                  {!!(item as any).badge&&<span style={{background:T.borderAccent,color:"#0f1117",fontSize:10,fontWeight:700,borderRadius:10,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{(item as any).badge}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    )}

    {/* ── Mobile: horizontal scroll tabs ── */}
    {isMobile&&(
      <div style={{display:"flex",overflowX:"auto" as any,borderBottom:`1px solid ${T.borderPrimary}`,background:T.bgPanel,WebkitOverflowScrolling:"touch" as any,flexShrink:0,position:"sticky" as any,top:0,zIndex:10}}>
        {([
          {id:"settings-profile",label:"Profile"},
          {id:"settings-team",label:"Team"},
          {id:"settings-costcodes",label:"Cost Codes"},
          {id:"settings-exclusions",label:"Exclusions"},
          {id:"settings-labor",label:"Labor"},
          {id:"settings-lessons",label:"Lessons"},
          {id:"settings-presets",label:"Presets"},
          {id:"settings-phase-groups",label:"Phases"},
          {id:"settings-integrations",label:"Integrations"},
          // {id:"settings-activity",label:"Activity"},  // hidden — see line ~7398
          ...(isAdmin ? [{id:"settings-access",label:"Beta Access"}] : []),
        ]).map(item=>{
          const active=activeTab===item.id;
          return(
            <button key={item.id} onClick={()=>setActiveTab(item.id)}
              style={{background:"none",border:"none",borderBottom:`2px solid ${active?T.borderAccent:"transparent"}`,
                color:active?T.textSecondary:T.textMuted,padding:"10px 14px",fontSize:11,fontFamily:FONT,
                cursor:"pointer",fontWeight:active?600:400,flexShrink:0,whiteSpace:"nowrap" as any}}>
              {item.label}
            </button>
          );
        })}
      </div>
    )}

    {/* ── Content pane ── */}
    <div style={{flex:1,overflowY:"auto",minWidth:0}}>
      {backLink}

    {/* ── PROFILE ── */}
    {activeTab==="settings-profile"&&user&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:540}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:6}}>Profile</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:20}}>Update your display name and manage account security.</div>
        <div className="settings-card">
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:T.borderSecondary,border:`2px solid ${T.borderHover}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,color:T.textTertiary,flexShrink:0}}>
          {user.avatarUrl?<img src={user.avatarUrl} alt="" style={{width:52,height:52,borderRadius:"50%"}}/>
            :(user.displayName||user.email||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}
        </div>
        <div>
          <div style={{fontSize:16,fontWeight:600,color:T.textSecondary}}>{user.displayName}</div>
          <div style={{fontSize:12,color:T.textWeak}}>{user.email}</div>
        </div>
      </div>
      <div className="settings-field">
        <label className="settings-label">Display Name</label>
        <input className="settings-input" value={editDisplayName} onChange={e=>setEditDisplayName(e.target.value)} placeholder="Your name"/>
      </div>
      <div className="settings-field">
        <label className="settings-label">Email</label>
        <input className="settings-input" value={user.email} readOnly/>
      </div>
      <div className="settings-field">
        <label className="settings-label">Plan</label>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{
            display:"inline-flex",alignItems:"center",gap:5,
            padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:700,letterSpacing:".06em",
            background:entitlements.plan==="premium"?`${T.borderAccent}22`:`${T.borderSecondary}44`,
            border:`1px solid ${entitlements.plan==="premium"?T.borderAccent:T.borderSecondary}`,
            color:entitlements.plan==="premium"?T.borderAccent:T.textTertiary,
          }}>
            {entitlements.plan==="premium"?"★ Premium":"Base"}
          </span>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <button className="settings-save-btn" disabled={profileSaving||editDisplayName===user.displayName}
          onClick={async()=>{
            setProfileSaving(true);setProfileMsg("");
            try{
              const {supabase}=await import("../lib/supabase");
              if(supabase){
                const {error}=await supabase.auth.updateUser({data:{display_name:editDisplayName}});
                if(error)setProfileMsg(error.message);
                else setProfileMsg("Saved! Changes take effect on next login.");
              }
            }catch(ex){setProfileMsg("Error saving profile");}
            finally{setProfileSaving(false);}
          }}>
          {profileSaving?"Saving...":"Save Changes"}
        </button>
        {profileMsg&&<span style={{fontSize:11,color:profileMsg.includes("Saved")?T.positive:T.negative}}>{profileMsg}</span>}
      </div>
    </div>
      </div>
    )}

    {/* ── TEAM ── */}
    {activeTab==="settings-team"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:720}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:6}}>Members &amp; Roles</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:20}}>Invite collaborators and control what each person can view or edit on this project.</div>

    {/* Create org */}
    <div className="create-org-row">
      <input className="invite-input" value={newOrgName} onChange={e=>setNewOrgName(e.target.value)}
        placeholder="New organization name..." onKeyDown={e=>{if(e.key==="Enter")createOrg();}}/>
      <button className="invite-btn" onClick={createOrg} disabled={!newOrgName.trim()}>Create</button>
    </div>
    {orgMsg&&<div style={{fontSize:11,color:T.negative,marginBottom:12,padding:"6px 10px",background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,borderRadius:3}}>{orgMsg}</div>}

    {orgsLoading?(
      <div style={{textAlign:"center",padding:"32px 0",color:T.textWeak,fontSize:12}}>Loading...</div>
    ):orgs.length===0?(
      <div style={{textAlign:"center",padding:"48px 0",border:`1px dashed ${T.borderPrimary}`,borderRadius:3}}>
        <div style={{fontSize:24,opacity:.15,marginBottom:10}}>&#128101;</div>
        <div style={{fontSize:13,color:T.textWeak,marginBottom:4}}>No organizations yet</div>
        <div style={{fontSize:11,color:T.textMuted}}>Create an organization to collaborate with your team</div>
      </div>
    ):(
      <div>
        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>My Organizations</div>
        {orgs.map(org=>{
          const pendingReqs=(ownershipRequests[org.id]||[]).filter(r=>r.requesterId!==user?.id);
          const myPendingReq=(ownershipRequests[org.id]||[]).find(r=>r.requesterId===user?.id);
          return (
          <div key={org.id} className="org-card">
            <div className="org-card-header">
              <div className="org-card-title">{org.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {org.myRole!=="owner"&&(
                  <button className="request-ownership-btn" disabled={!!myPendingReq}
                    onClick={()=>requestOwnership(org.id)}>
                    {myPendingReq?"Ownership Requested":"Request Ownership"}
                  </button>
                )}
                <span className="org-role-badge">{org.myRole}</span>
              </div>
            </div>

            {/* Pending ownership requests (visible to owner) */}
            {org.myRole==="owner"&&pendingReqs.length>0&&(
              <div className="request-banner">
                <div style={{fontSize:10,color:T.borderAccent,letterSpacing:".08em",textTransform:"uppercase",marginBottom:6,fontWeight:600}}>Pending Ownership Requests</div>
                {pendingReqs.map(req=>(
                  <div key={req.id} className="request-row">
                    <div style={{flex:1}}>
                      <span className="request-name">{req.name||req.email}</span>
                      {req.name&&<span className="request-email" style={{marginLeft:8}}>{req.email}</span>}
                    </div>
                    <button className="approve-btn" onClick={()=>confirmApproveTransfer(req.id,org.id,req.requesterId,req.name||req.email)}>Approve</button>
                    <button className="deny-btn" onClick={()=>respondToRequest(req.id,org.id,req.requesterId,false)}>Deny</button>
                  </div>
                ))}
              </div>
            )}

            {/* Members */}
            <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Members ({org.members.length})</div>
            {org.members.map(mem=>(
              <div key={mem.userId} className="member-row">
                <div className="member-avatar">
                  {(mem.displayName||mem.email||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}
                </div>
                <div className="member-name">
                  <div>{mem.displayName||mem.email}{mem.userId===user?.id&&<span style={{color:T.textWeak,fontSize:10}}> (you)</span>}</div>
                  {mem.displayName&&<div className="email">{mem.email}</div>}
                </div>
                {mem.userId===user?.id||org.myRole==="member"?(
                  <span style={{fontSize:10,color:T.textWeak,textTransform:"capitalize"}}>{mem.role}</span>
                ):(
                  <select className="role-select" value={mem.role} onChange={e=>changeOrgRole(org.id,mem.userId,e.target.value)}>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                )}
                {mem.userId!==user?.id&&(org.myRole==="owner"||org.myRole==="admin")&&(
                  <button className="remove-btn" onClick={()=>confirmRemoveMember(org.id,org.name,mem.displayName||mem.email,mem.userId)} title="Remove member">&times;</button>
                )}
              </div>
            ))}

            {/* Pending invitations */}
            {(org.pendingInvites||[]).length>0&&(
              <>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6,marginTop:12}}>Pending Invitations ({org.pendingInvites.length})</div>
                {(org.pendingInvites||[]).map(inv=>(
                  <div key={inv.id} className="member-row" style={{opacity:0.7}}>
                    <div className="member-avatar" style={{background:T.borderPrimary,color:T.textWeak}}>✉</div>
                    <div className="member-name">
                      <div style={{color:T.textTertiary}}>{inv.email}</div>
                      <div className="email">Invitation sent · awaiting signup</div>
                    </div>
                    <span style={{fontSize:10,color:T.borderAccent,textTransform:"capitalize",border:`1px solid ${T.bgAccentSubtle}`,borderRadius:3,padding:"1px 6px"}}>{inv.role}</span>
                    {(org.myRole==="owner"||org.myRole==="admin")&&(
                      <button className="remove-btn" onClick={()=>revokeInvite(org.id,inv.id)} title="Revoke invitation">&times;</button>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Invite */}
            {(org.myRole==="owner"||org.myRole==="admin")&&(
              <div className="invite-row">
                <input className="invite-input" value={inviteEmails[org.id]||""} onChange={e=>setInviteEmails(prev=>({...prev,[org.id]:e.target.value}))}
                  placeholder="Invite by email..." onKeyDown={e=>{if(e.key==="Enter")inviteMember(org.id);}}/>
                <select className="role-select" value={inviteRoles[org.id]||"member"} onChange={e=>setInviteRoles(prev=>({...prev,[org.id]:e.target.value}))}>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button className="invite-btn" onClick={()=>inviteMember(org.id)} disabled={!(inviteEmails[org.id]||"").trim()}>Invite</button>
              </div>
            )}

            {/* Company Logo (owner only) */}
            {org.myRole==="owner"&&(
              <div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${T.borderPrimary}`}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8}}>Company Logo <span style={{color:T.textMuted,fontStyle:"normal",textTransform:"none",letterSpacing:0}}>(appears on print sheets)</span></div>
                <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  {orgLogos[org.id]?(
                    <div style={{position:"relative",display:"inline-block"}}>
                      <img src={orgLogos[org.id]} alt="Logo" style={{maxHeight:52,maxWidth:160,objectFit:"contain",borderRadius:3,background:"#fff",padding:4,border:`1px solid ${T.borderSecondary}`}}/>
                      <button onClick={()=>clearOrgLogo(org.id)} title="Remove logo"
                        style={{position:"absolute",top:-7,right:-7,width:16,height:16,borderRadius:"50%",background:T.negative,border:"none",color:"#fff",cursor:"pointer",fontSize:10,display:"flex",alignItems:"center",justifyContent:"center"}}><Icon.X size={10}/></button>
                    </div>
                  ):(
                    <div style={{width:100,height:48,border:`2px dashed ${T.borderSecondary}`,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textWeak}}>No logo</div>
                  )}
                  <div>
                    <label style={{display:"inline-block",cursor:"pointer",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,padding:"5px 12px",fontSize:11,fontFamily:FONT,fontWeight:500}}>
                      {orgLogos[org.id]?"Replace Logo":"Upload Logo"}
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                        const file=e.target.files?.[0];if(!file)return;
                        if(file.size>500_000){alert("Logo must be under 500 KB");return;}
                        const reader=new FileReader();
                        reader.onload=ev=>{const r=ev.target?.result;if(typeof r==="string")setOrgLogo(org.id,r);};
                        reader.readAsDataURL(file);
                        e.target.value="";
                      }}/>
                    </label>
                    <div style={{fontSize:10,color:T.textWeak,marginTop:4}}>PNG, JPG, or SVG · max 500 KB</div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete org (owner only) */}
            {org.myRole==="owner"&&(
              <div className="danger-section">
                <button className="danger-btn" onClick={()=>confirmDeleteOrg(org.id,org.name)}>Delete Organization</button>
              </div>
            )}
          </div>
          );
        })}
      </div>
    )}

      </div>
    )}

    {/* ── COST CODES ── */}
    {activeTab==="settings-costcodes"&&(
      <CostCodeLibrary T={T} isMobile={isMobile} isTablet={isTablet}
        userOrgs={(orgs||[]).map((o:any)=>({id:o.id,name:o.name}))}
        currentOrgId={orgs?.[0]?.id??null} showToast={showToast}/>
    )}

    {/* ── EXCLUSIONS ── */}
    {activeTab==="settings-exclusions"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:640}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:8}}>Standard Exclusions</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:20,lineHeight:1.6}}>
          Your organization's reusable list of standard exclusions. From any project's Summary tab, you can add these to the Project Exclusions in one click.
        </div>
        {orgExclusions.map((ex)=>(
          <div key={ex.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"8px 12px",background:T.bgPanel,borderRadius:3,border:`1px solid ${T.borderPrimary}`}}>
            <span style={{flex:1,fontSize:12,color:T.textSecondary,lineHeight:1.4}}>{ex.text}</span>
            <button onClick={()=>setOrgExclusions(p=>p.filter(x=>x.id!==ex.id))}
              style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 2px",opacity:.5,flexShrink:0}}>&times;</button>
          </div>
        ))}
        <div style={{display:"flex",gap:6,alignItems:"center",marginTop:10}}>
          <input className="ghost-input" id="new-org-excl-settings" placeholder="Add a standard exclusion to the library…"
            onKeyDown={e=>{if(e.key==="Enter"){const inp=e.currentTarget;if(inp.value.trim()){setOrgExclusions(p=>[...p,{id:uid(),text:inp.value.trim()}]);inp.value="";}}}}
            style={{flex:1,fontSize:11,padding:"7px 10px",border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:T.bgInput,color:T.textSecondary,fontFamily:FONT}}/>
          <button className="add-row-btn" onClick={()=>{const inp=document.getElementById("new-org-excl-settings") as HTMLInputElement;if(inp?.value.trim()){setOrgExclusions(p=>[...p,{id:uid(),text:inp.value.trim()}]);inp.value="";}}}
            style={{flexShrink:0,whiteSpace:"nowrap" as any}}>+ Add</button>
        </div>
        <button style={{marginTop:10,fontSize:10,color:T.textWeak,background:"none",border:`1px solid ${T.borderSecondary}`,padding:"4px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT}}
          onClick={()=>setOrgExclusions(DEFAULT_ORG_EXCLUSIONS)}><Icon.RefreshCcw size={12}/> Reset to defaults</button>
      </div>
    )}

    {/* ── LABOR RATES ── */}
    {activeTab==="settings-labor"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:480}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:8}}>Labor Rate Library</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:18,lineHeight:1.6}}>
          Standard labor rates ($/hr) available org-wide. Use the <strong style={{color:T.textMuted}}>+ Add Labor</strong> button in any subcategory to insert a pre-filled labor line item.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:"6px 8px",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:600,color:T.textWeak,textTransform:"uppercase" as any,letterSpacing:.5}}>Role</div>
          <div style={{fontSize:10,fontWeight:600,color:T.textWeak,textTransform:"uppercase" as any,letterSpacing:.5,textAlign:"right" as any,minWidth:70}}>$/hr</div>
          <div/>
          {laborRates.map(lr=>(
            <React.Fragment key={lr.id}>
              <input className="ghost-input" value={lr.role}
                onChange={e=>setLaborRates(p=>p.map(x=>x.id===lr.id?{...x,role:e.target.value}:x))}
                style={{fontSize:12,padding:"5px 8px",border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:T.bgInput,color:T.textSecondary,fontFamily:FONT}}/>
              <input className="ghost-input" type="number" min={0} value={lr.rate}
                onChange={e=>setLaborRates(p=>p.map(x=>x.id===lr.id?{...x,rate:parseFloat(e.target.value)||0}:x))}
                style={{fontSize:12,padding:"5px 8px",border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:T.bgInput,color:T.textSecondary,fontFamily:FONT,textAlign:"right" as any,width:70}}/>
              <button onClick={()=>setLaborRates(p=>p.filter(x=>x.id!==lr.id))}
                style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 4px",opacity:.5}}>&times;</button>
            </React.Fragment>
          ))}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6}}>
          <button className="add-row-btn" onClick={()=>setLaborRates(p=>[...p,{id:uid(),role:"New Role",rate:0}])}
            style={{whiteSpace:"nowrap" as any}}>+ Add Role</button>
          <button style={{fontSize:10,color:T.textWeak,background:"none",border:`1px solid ${T.borderSecondary}`,padding:"4px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT}}
            onClick={()=>setLaborRates(DEFAULT_LABOR_RATES)}><Icon.RefreshCcw size={12}/> Reset to defaults</button>
        </div>
      </div>
    )}

    {/* ── LESSONS ── */}
    {activeTab==="settings-lessons"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:720}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:20,fontWeight:700,color:T.textSecondary}}>Lessons &amp; Best Practices</div>
          <button className="add-row-btn"
            onClick={()=>setBpOrgEditItem({id:`bp${Date.now()}`,description:"",divisionTags:[],category:"eo",addedAt:new Date().toISOString().slice(0,10)})}>
            + New Entry
          </button>
        </div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:16,lineHeight:1.6}}>
          Your organization's reusable checklist of scope gaps, E&amp;O risks, VE opportunities, and code requirements. Entries surface as tips while you build estimates division by division.
        </div>

      {/* Category filter pills */}
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:14}}>
        {[{id:"all",label:"All",color:T.borderAccent,bg:T.bgAccentSubtle},...BP_CATEGORIES].map(c=>{
          const active=bpOrgFilterCat===c.id;
          const count=c.id==="all"?bestPractices.length:bestPractices.filter(b=>b.category===c.id).length;
          return(
            <button key={c.id} onClick={()=>setBpOrgFilterCat(c.id)}
              style={{padding:"3px 11px",borderRadius:999,border:`1px solid ${active?(c as any).color:T.borderSecondary}`,background:active?(c as any).bg:"transparent",color:active?(c as any).color:T.textMuted,cursor:"pointer",fontFamily:FONT,fontSize:10,fontWeight:active?700:400}}>
              {c.label} <span style={{opacity:.6}}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Entry list */}
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {bestPractices.filter(bp=>bpOrgFilterCat==="all"||bp.category===bpOrgFilterCat).map(bp=>{
          const cat=BP_CATEGORIES.find(c=>c.id===bp.category)||BP_CATEGORIES[0];
          const divLabels=bp.divisionTags.map(d=>BP_ALL_DIVS.find(x=>x.id===d)?.code||d);
          return(
            <div key={bp.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 12px",background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6}}>
              <div style={{width:3,borderRadius:3,background:cat.color,flexShrink:0,alignSelf:"stretch",minHeight:16}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap",marginBottom:4}}>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:".05em",color:cat.color,background:cat.bg,border:`1px solid ${cat.color}44`,borderRadius:10,padding:"1px 8px",flexShrink:0}}>{cat.label}</span>
                  {divLabels.map(dl=><span key={dl} style={{fontSize:10,color:T.textMuted,background:T.bgApp,border:`1px solid ${T.borderSecondary}`,borderRadius:3,padding:"1px 6px"}}>{dl}</span>)}
                </div>
                <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.5}}>{bp.description}</div>
                {bp.sourceNote&&<div style={{fontSize:10,color:T.textWeak,fontStyle:"italic",marginTop:2}}>↳ {bp.sourceNote}</div>}
              </div>
              <div style={{display:"flex",gap:4,flexShrink:0}}>
                <button onClick={()=>setBpOrgEditItem({...bp})}
                  style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,padding:"3px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:10}}>Edit</button>
                {bpOrgDeleteConfirmId===bp.id?(
                  <>
                    <button onClick={()=>{setBestPractices(p=>p.filter(x=>x.id!==bp.id));setBpOrgDeleteConfirmId(null);}}
                      style={{background:"#fc8181",border:"none",color:"#fff",padding:"3px 9px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:10,fontWeight:700}}>Delete</button>
                    <button onClick={()=>setBpOrgDeleteConfirmId(null)}
                      style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,padding:"3px 9px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:10}}>Cancel</button>
                  </>
                ):(
                  <button onClick={()=>setBpOrgDeleteConfirmId(bp.id)}
                    style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,padding:"3px 8px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:14,lineHeight:1}}>&times;</button>
                )}
              </div>
            </div>
          );
        })}
        {bestPractices.filter(bp=>bpOrgFilterCat==="all"||bp.category===bpOrgFilterCat).length===0&&(
          <div style={{textAlign:"center",padding:"24px 0",color:T.textWeak,fontSize:12}}>No entries in this category</div>
        )}
      </div>

        <div style={{display:"flex",gap:8,marginTop:12}}>
          <button style={{fontSize:10,color:T.textWeak,background:"none",border:`1px solid ${T.borderSecondary}`,padding:"4px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT}}
            onClick={()=>{if(window.confirm("Reset to default library? Your custom entries will be replaced."))setBestPractices(DEFAULT_BEST_PRACTICES);}}><Icon.RefreshCcw size={12}/> Reset to defaults</button>
        </div>
        {/* BP add/edit modal */}
        {bpOrgEditItem&&(
          <BPModal item={bpOrgEditItem} T={T}
            onSave={bp=>{
              setBestPractices(prev=>{const idx=prev.findIndex(x=>x.id===bp.id);if(idx>=0){const n=[...prev];n[idx]=bp;return n;}return[...prev,bp];});
              setBpOrgEditItem(null);
            }}
            onClose={()=>setBpOrgEditItem(null)}/>
        )}
      </div>
    )}

    {/* ── PRESETS ── */}
    {activeTab==="settings-presets"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:760}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:8}}>Presets</div>
        <div style={{fontSize:11,color:T.textWeak,marginBottom:20}}>
          Presets define the starting divisions and markups applied when creating a new project. Built-in presets can be duplicated and customized.
        </div>

    {/* New preset form */}
    {showNewPresetForm?(
      <div style={{background:T.bgPanel,border:`1px solid ${T.borderAccent}44`,borderRadius:6,padding:"14px 16px",marginBottom:16,display:"flex",gap:8,alignItems:"center"}}>
        <input className="invite-input" style={{flex:1}} value={newPresetName} onChange={e=>setNewPresetName(e.target.value)}
          placeholder="Preset name (e.g. Luxury Residential)" autoFocus
          onKeyDown={e=>{if(e.key==="Enter")addPreset(newPresetName);if(e.key==="Escape"){setShowNewPresetForm(false);setNewPresetName("");}}}/>
        <button className="invite-btn" onClick={()=>addPreset(newPresetName)} disabled={!newPresetName.trim()}>Create</button>
        <button onClick={()=>{setShowNewPresetForm(false);setNewPresetName("");}}
          style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"6px 12px",borderRadius:3,fontFamily:FONT,fontSize:12}}>Cancel</button>
      </div>
    ):(
      <button className="invite-btn" style={{marginBottom:20}} onClick={()=>setShowNewPresetForm(true)}>+ New Preset</button>
    )}

    {presets.map(p=>{
      const isEditing=editingPresetId===p.id;
      const divCount=(p.divisions||[]).length;
      return (
        <div key={p.id} className="org-card" style={{marginBottom:12}}>
          <div className="org-card-header">
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div className="org-card-title">{p.name}</div>
              {p.builtIn&&<span style={{fontSize:10,color:T.textWeak,background:T.bgToast,border:`1px solid ${T.borderSecondary}`,borderRadius:3,padding:"1px 6px",letterSpacing:".08em"}}>BUILT-IN</span>}
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:11,color:T.textWeak}}>{divCount} division{divCount!==1?"s":""} · OH {p.overhead}% · Profit {p.profit}% · Cont {p.contingency}%</span>
              <button onClick={()=>duplicatePreset(p)}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"3px 10px",borderRadius:3,fontFamily:FONT,fontSize:11}}>Duplicate</button>
              <button onClick={()=>setEditingPresetId(isEditing?null:p.id)}
                style={{background:"none",border:`1px solid ${isEditing?`${T.borderAccent}55`:T.borderSecondary}`,color:isEditing?T.borderAccent:T.textMuted,cursor:"pointer",padding:"3px 10px",borderRadius:3,fontFamily:FONT,fontSize:11}}>
                {isEditing?"Done":"Edit"}</button>
              {!p.builtIn&&<button onClick={()=>{if(editingPresetId===p.id)setEditingPresetId(null);deletePreset(p.id);}}
                style={{background:"none",border:`1px solid ${T.dangerBorder}`,color:T.dangerText,cursor:"pointer",padding:"3px 10px",borderRadius:3,fontFamily:FONT,fontSize:11}}>Delete</button>}
            </div>
          </div>

          {isEditing&&(
            <div style={{padding:"14px 0 4px"}}>
              {/* Name edit (non-built-in only) */}
              {!p.builtIn&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Preset Name</div>
                  <input className="input-field" style={{width:"100%"}} value={p.name}
                    onChange={e=>savePreset({...p,name:e.target.value})}/>
                </div>
              )}
              {/* Markups */}
              <div style={{background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:3,padding:"12px 14px",marginBottom:12}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Default Markups</div>
                <div style={{display:"flex",gap:10}}>
                  {[["Overhead %","overhead"],["Profit %","profit"],["Contingency %","contingency"]].map(([lbl,field])=>(
                    <div key={field} style={{flex:1}}>
                      <div style={{fontSize:10,color:T.textWeak,marginBottom:4}}>{lbl}</div>
                      <input type="number" step=".5" min="0" max="100" className="input-field"
                        value={p[field]??0}
                        disabled={p.builtIn}
                        onChange={e=>savePreset({...p,[field]:+e.target.value})}
                        style={{width:"100%",opacity:p.builtIn?.6:1}}/>
                    </div>
                  ))}
                </div>
                {p.builtIn&&<div style={{fontSize:10,color:T.textWeak,marginTop:8}}>Duplicate this preset to customize markups.</div>}
              </div>
              {/* Divisions */}
              <div style={{background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:3,padding:"12px 14px"}}>
                <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Active Divisions</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                  {ALL_DIVISIONS.map(div=>{
                    const isOn=(p.divisions||[]).includes(div.id);
                    return (
                      <div key={div.id} onClick={()=>{if(p.builtIn)return;savePreset({...p,divisions:isOn?(p.divisions||[]).filter(d=>d!==div.id):[...(p.divisions||[]),div.id]});}}
                        style={{display:"flex",alignItems:"center",gap:7,padding:"5px 7px",cursor:p.builtIn?"default":"pointer",borderRadius:3,
                          background:isOn?T.bgAccentFaint:"transparent",transition:"background .12s",opacity:p.builtIn&&!isOn?.4:1}}>
                        <div style={{width:11,height:11,borderRadius:3,flexShrink:0,
                          border:`1px solid ${isOn?T.borderAccent:T.borderHover}`,background:isOn?T.borderAccent:"none"}}/>
                        <span style={{fontSize:10,color:isOn?T.textSecondary:T.textWeak}}>
                          <span style={{color:T.textMuted,marginRight:4}}>{div.code}</span>{div.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {p.builtIn&&<div style={{fontSize:10,color:T.textWeak,marginTop:8}}>Duplicate this preset to customize divisions.</div>}
              </div>
            </div>
          )}
        </div>
      );
    })}
      </div>
    )}

    {/* ── ACTIVITY LOG ── */}
    {activeTab==="settings-activity"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:760}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:6}}>Activity Log</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:24}}>
          A record of all changes made to this project — who changed what and when.
        </div>
        <ActivityLogPane T={T} projectId={cloudProjectId} user={user}/>
      </div>
    )}

    {/* ── PHASE GROUPS ── */}
    {activeTab==="settings-phase-groups"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:700}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:6}}>Phases</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:20,lineHeight:1.7,maxWidth:520}}>
          A phase maps CSI MasterFormat divisions to an owner-friendly label — like <em>Structure &amp; Framing</em> or <em>MEP</em>. Phases group the Schedule and Cash Flow tabs, colour their bars, and set the order Auto-Distribute lays work out in, so reordering them here changes the schedule it produces. A line item's phase follows its division; there is no per-item setting. The underlying data always ties back to CSI divisions.
        </div>

        {/* Group list */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {[...phaseGroups].sort((a,b)=>a.order-b.order).map(pg=>{
            const isEditing=pgEditId===pg.id;
            const divPickerOpen=pgDivOpen===pg.id;
            // divisions already assigned to OTHER groups (to show as unavailable)
            const assignedElsewhere=new Set(phaseGroups.filter(g=>g.id!==pg.id).flatMap(g=>g.divIds));
            const assignedDivLabels=pg.divIds.map(did=>{const d=ALL_DIVISIONS.find(x=>x.id===did);return d?`${d.code}`:"?"});
            return (
              <div key={pg.id} style={{background:T.bgPanel,border:`1px solid ${isEditing?`${pg.color}66`:T.borderPrimary}`,borderRadius:6,overflow:"hidden",transition:"border-color .15s"}}>
                {/* Row header */}
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px"}}>
                  {/* Color swatch / picker */}
                  <label title="Change color" style={{flexShrink:0,cursor:"pointer"}}>
                    <div style={{width:14,height:14,borderRadius:"50%",background:pg.color,boxShadow:`0 0 0 2px ${T.bgPanel},0 0 0 3px ${pg.color}66`,cursor:"pointer"}}/>
                    <input type="color" value={pg.color} style={{display:"none"}}
                      onChange={e=>setPhaseGroups(prev=>prev.map(g=>g.id===pg.id?{...g,color:e.target.value}:g))}/>
                  </label>

                  {/* Name */}
                  {isEditing?(
                    <input value={pg.name} autoFocus
                      onChange={e=>setPhaseGroups(prev=>prev.map(g=>g.id===pg.id?{...g,name:e.target.value}:g))}
                      onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape")setPgEditId(null);}}
                      style={{flex:1,fontSize:13,fontWeight:600,padding:"3px 8px",border:`1px solid ${T.borderAccent}`,borderRadius:3,background:T.bgInput,color:T.textSecondary,fontFamily:FONT,outline:"none"}}/>
                  ):(
                    <span style={{flex:1,fontSize:13,fontWeight:600,color:T.textSecondary,cursor:"text"}}
                      onDoubleClick={()=>setPgEditId(pg.id)}>{pg.name}</span>
                  )}

                  {/* Division count chips (collapsed) */}
                  {!isEditing&&(
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",flex:"0 0 auto",maxWidth:300,justifyContent:"flex-end"}}>
                      {assignedDivLabels.slice(0,6).map(lbl=>(
                        <span key={lbl} style={{fontSize:10,color:T.textWeak,background:T.bgApp,border:`1px solid ${T.borderSecondary}`,borderRadius:3,padding:"1px 6px",whiteSpace:"nowrap"}}>{lbl}</span>
                      ))}
                      {assignedDivLabels.length>6&&<span style={{fontSize:10,color:T.textWeak}}>+{assignedDivLabels.length-6} more</span>}
                    </div>
                  )}

                  {/* Edit / Done button */}
                  <button onClick={()=>setPgEditId(isEditing?null:pg.id)}
                    style={{background:"none",border:`1px solid ${isEditing?T.borderAccent:T.borderSecondary}`,color:isEditing?T.borderAccent:T.textMuted,padding:"3px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:10,flexShrink:0}}>
                    {isEditing?"Done":"Edit"}
                  </button>

                  {/* Delete (only if not last group) */}
                  {phaseGroups.length>1&&(
                    <button onClick={()=>{
                      if(!window.confirm(`Delete "${pg.name}"? Divisions will become unassigned.`))return;
                      setPhaseGroups(prev=>prev.filter(g=>g.id!==pg.id));
                      if(pgEditId===pg.id)setPgEditId(null);
                    }} style={{background:"none",border:`1px solid ${T.dangerBorder}`,color:T.dangerText,padding:"3px 8px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontSize:12,flexShrink:0,lineHeight:1}}>&times;</button>
                  )}
                </div>

                {/* Expanded editor */}
                {isEditing&&(
                  <div style={{padding:"0 14px 14px"}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8}}>Assigned Divisions</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:4}}>
                      {ALL_DIVISIONS.map(div=>{
                        const isAssigned=pg.divIds.includes(div.id);
                        const takenByOther=assignedElsewhere.has(div.id)&&!isAssigned;
                        return (
                          <div key={div.id}
                            onClick={()=>{
                              if(takenByOther){
                                // Steal from other group
                                setPhaseGroups(prev=>prev.map(g=>{
                                  if(g.id===pg.id)return{...g,divIds:[...g.divIds,div.id]};
                                  return{...g,divIds:g.divIds.filter(d=>d!==div.id)};
                                }));
                              } else {
                                setPhaseGroups(prev=>prev.map(g=>g.id!==pg.id?g:{
                                  ...g,divIds:isAssigned?g.divIds.filter(d=>d!==div.id):[...g.divIds,div.id]
                                }));
                              }
                            }}
                            style={{display:"flex",alignItems:"center",gap:7,padding:"5px 7px",cursor:"pointer",borderRadius:3,
                              background:isAssigned?`${pg.color}22`:takenByOther?T.bgApp:"transparent",
                              border:`1px solid ${isAssigned?`${pg.color}55`:T.borderPrimary}`,
                              transition:"background .1s",opacity:takenByOther?.55:1}}>
                            <div style={{width:10,height:10,borderRadius:3,flexShrink:0,
                              border:`1px solid ${isAssigned?pg.color:T.borderHover}`,background:isAssigned?pg.color:"none"}}/>
                            <span style={{fontSize:10,color:isAssigned?T.textSecondary:T.textWeak,lineHeight:1.3}}>
                              <span style={{color:isAssigned?pg.color:`${pg.color}77`,marginRight:3,fontWeight:600}}>{div.code}</span>
                              {div.name}
                            </span>
                            {takenByOther&&<span style={{fontSize:10,color:T.textMuted,marginLeft:"auto",flexShrink:0}}>→ move here</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add new group */}
        <button className="add-row-btn" onClick={()=>{
          const newId=`pg_${Date.now()}`;
          const colors=["#ec4899","#14b8a6","#a855f7","#f43f5e","#22d3ee","#84cc16"];
          const usedColors=new Set(phaseGroups.map(g=>g.color));
          const nextColor=colors.find(c=>!usedColors.has(c))||"#6b7280";
          setPhaseGroups(prev=>[...prev,{id:newId,name:"New Phase",color:nextColor,divIds:[],order:prev.length}]);
          setPgEditId(newId);
        }} style={{marginRight:8}}>+ Add Group</button>

        <button style={{fontSize:10,color:T.textWeak,background:"none",border:`1px solid ${T.borderSecondary}`,padding:"5px 12px",borderRadius:3,cursor:"pointer",fontFamily:FONT}}
          onClick={()=>{if(window.confirm("Reset to the default phases? Your custom phases will be replaced."))setPhaseGroups(DEFAULT_PHASE_GROUPS);}}>
          <Icon.RefreshCcw size={12}/> Reset to defaults
        </button>

        <div style={{marginTop:20,padding:"12px 14px",background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,fontSize:11,color:T.textWeak,lineHeight:1.65}}>
          <strong style={{color:T.textTertiary}}>Tip:</strong> Double-click a name to rename it. Click the color circle to recolor. In the division editor, clicking a division from another group will <em>move</em> it here.
        </div>
      </div>
    )}

    {activeTab==="settings-integrations"&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:760}}>
        <div style={{fontSize:20,fontWeight:700,color:T.textSecondary,marginBottom:6}}>Integrations</div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:32}}>
          Connect third-party platforms to push estimate data without leaving OpenEstimate.
        </div>

        {/* Procore card */}
        <div style={{background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:10,padding:"24px 28px",marginBottom:20,display:"flex",flexDirection:isMobile?"column":"row",alignItems:isMobile?"flex-start":"center",gap:isMobile?20:0,justifyContent:"space-between"}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
              <div style={{width:40,height:40,borderRadius:10,background:"#f04e37",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{color:"#fff",fontSize:16,fontWeight:700}}>P</span>
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.textSecondary}}>Procore</div>
                <div style={{fontSize:11,color:T.textWeak}}>Construction project management</div>
              </div>
            </div>
            <div style={{fontSize:12,color:T.textTertiary,lineHeight:1.65,maxWidth:420}}>
              Push your completed estimate directly into a Procore project as a budget. Line items are mapped to Procore cost codes by CSI division. Connect once — export any time from <strong style={{color:T.textSecondary}}>File → Export → Procore</strong>.
            </div>
          </div>
          <div style={{flexShrink:0,marginLeft:isMobile?0:32,textAlign:isMobile?"left":"right"}}>
            {procoreConnected===null?(
              <div style={{fontSize:11,color:T.textWeak}}>Checking…</div>
            ):procoreConnected?(
              <div style={{display:"flex",flexDirection:"column",alignItems:isMobile?"flex-start":"flex-end",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:"#48bb78",display:"inline-block"}}/>
                  <span style={{fontSize:12,color:"#48bb78",fontWeight:600}}>Connected</span>
                </div>
                <button
                  onClick={()=>{setProcoreConnected(null);handleProcoreConnect();}}
                  disabled={procoreConnecting}
                  style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:FONT}}>
                  Reconnect
                </button>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",alignItems:isMobile?"flex-start":"flex-end",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:T.textWeak,display:"inline-block"}}/>
                  <span style={{fontSize:12,color:T.textWeak}}>Not connected</span>
                </div>
                <button
                  onClick={handleProcoreConnect}
                  disabled={procoreConnecting}
                  style={{background:T.borderAccent,border:"none",color:"#0f1117",padding:"8px 18px",borderRadius:6,cursor:procoreConnecting?"default":"pointer",fontSize:12,fontWeight:700,fontFamily:FONT,opacity:procoreConnecting?.6:1}}>
                  {procoreConnecting?"Connecting…":"Connect Procore"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Future integrations placeholder */}
        <div style={{fontSize:11,color:T.textWeak,marginTop:28,padding:"16px 20px",border:`1px dashed ${T.borderPrimary}`,borderRadius:6,textAlign:"center"}}>
          More integrations coming — Sage, Viewpoint, Autodesk Construction Cloud, and QuickBooks are on the roadmap.
        </div>
      </div>
    )}

    {/* ── BETA ACCESS (admin only) ── */}
    {activeTab==="settings-access"&&isAdmin&&(
      <div style={{padding:isMobile?"16px":isTablet?"24px 28px":"36px 44px",maxWidth:640}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
          <div style={{fontSize:20,fontWeight:700,color:T.textSecondary}}>Beta Access</div>
          {(accessRequests||[]).filter(r=>r.status==="pending").length>0&&(
            <span style={{background:T.borderAccent,color:"#0f1117",fontSize:10,fontWeight:700,borderRadius:10,padding:"2px 8px"}}>
              {(accessRequests||[]).filter(r=>r.status==="pending").length} pending
            </span>
          )}
        </div>
        <div style={{fontSize:12,color:T.textWeak,marginBottom:20}}>
          Approve or reject early access requests. Approved users receive a signup link by email and are auto-approved on signup — they'll also need to confirm their email address before they can sign in.
        </div>

        {/* ── VIP Invite form ── */}
        <div style={{background:T.bgPanel,border:`1px solid ${T.borderAccent}`,borderRadius:6,padding:"16px 18px",marginBottom:24}}>
          <div style={{fontSize:12,fontWeight:700,color:T.textSecondary,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14}}>✉</span> Send a VIP Invite
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap" as any}}>
            <input
              placeholder="Name (optional)"
              value={inviteName}
              onChange={e=>setInviteName(e.target.value)}
              style={{flex:"1 1 140px",minWidth:0,background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
                padding:"7px 10px",fontSize:12,fontFamily:FONT,color:T.textSecondary,outline:"none"}}
            />
            <input
              placeholder="Email address"
              type="email"
              value={inviteEmail}
              onChange={e=>setInviteEmail(e.target.value)}
              onKeyDown={async e=>{
                if(e.key==="Enter"&&inviteEmail.trim()){
                  setInviteSending(true);
                  try{ await onInviteUser?.(inviteName.trim(),inviteEmail.trim()); setInviteName(""); setInviteEmail(""); }
                  finally{ setInviteSending(false); }
                }
              }}
              style={{flex:"2 1 200px",minWidth:0,background:T.bgApp,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
                padding:"7px 10px",fontSize:12,fontFamily:FONT,color:T.textSecondary,outline:"none"}}
            />
            <button
              disabled={inviteSending||!inviteEmail.trim()}
              onClick={async()=>{
                setInviteSending(true);
                try{ await onInviteUser?.(inviteName.trim(),inviteEmail.trim()); setInviteName(""); setInviteEmail(""); }
                finally{ setInviteSending(false); }
              }}
              style={{padding:"7px 16px",borderRadius:6,border:"none",cursor:inviteSending||!inviteEmail.trim()?"default":"pointer",
                fontFamily:FONT,fontSize:12,fontWeight:700,background:T.borderAccent,color:"#0f1117",
                opacity:inviteSending||!inviteEmail.trim()?.5:1,transition:"opacity .15s",flexShrink:0,whiteSpace:"nowrap" as any}}>
              {inviteSending?"Sending…":"Send Invite →"}
            </button>
          </div>
          <div style={{fontSize:10,color:T.textWeak,marginTop:8}}>
            They'll get a signup link by email and be approved instantly when they create their account, then a second email to confirm their address before they can sign in.
          </div>
        </div>

        {/* Status tabs */}
        <div style={{display:"flex",gap:2,marginBottom:20,background:T.bgToast,borderRadius:6,padding:3,width:"fit-content"}}>
          {(["pending","approved","rejected"] as const).map(tab=>{
            const count=(accessRequests||[]).filter(r=>r.status===tab).length;
            const active=accessStatusTab===tab;
            return(
              <button key={tab} onClick={()=>setAccessStatusTab(tab)}
                style={{padding:"5px 14px",borderRadius:6,border:"none",cursor:"pointer",fontFamily:FONT,
                  fontSize:11,fontWeight:active?700:400,
                  background:active?T.bgPanel:"transparent",
                  color:active?T.textSecondary:T.textWeak,
                  boxShadow:active?`0 1px 3px ${T.bgApp}55`:"none",
                  transition:"all .15s",display:"flex",alignItems:"center",gap:5}}>
                {tab.charAt(0).toUpperCase()+tab.slice(1)}
                {count>0&&<span style={{
                  background:tab==="pending"?T.borderAccent:T.borderPrimary,
                  color:tab==="pending"?"#0f1117":T.textWeak,
                  fontSize:10,fontWeight:700,borderRadius:10,padding:"1px 5px",minWidth:14,textAlign:"center"
                }}>{count}</span>}
              </button>
            );
          })}
        </div>

        {accessRequestsLoading?(
          <div style={{color:T.textWeak,fontSize:12,padding:"24px 0"}}>Loading…</div>
        ):(()=>{
          const filtered=(accessRequests||[]).filter(r=>r.status===accessStatusTab);
          if(filtered.length===0){
            return(
              <div style={{padding:"32px 0",textAlign:"center",color:T.textWeak,fontSize:12}}>
                No {accessStatusTab} requests
              </div>
            );
          }
          return(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {filtered.map(req=>{
                const inFlight=accessActionInFlight===req.email;
                const ts=new Date(req.created_at);
                const age=Date.now()-ts.getTime();
                const ageStr=age<3600000?`${Math.round(age/60000)}m ago`:age<86400000?`${Math.round(age/3600000)}h ago`:`${Math.round(age/86400000)}d ago`;
                return(
                  <div key={req.id} style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"14px 16px"}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                      {/* Avatar placeholder */}
                      <div style={{width:34,height:34,borderRadius:"50%",background:T.bgAccentFaint,border:`1px solid ${T.borderAccent}`,
                        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                        fontSize:13,fontWeight:700,color:T.borderAccent}}>
                        {(req.name||"?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" as any,marginBottom:2}}>
                          <span style={{fontSize:13,fontWeight:600,color:T.textSecondary}}>{req.name}</span>
                          <span style={{fontSize:11,color:T.textWeak}}>{ageStr}</span>
                        </div>
                        <div style={{fontSize:11,color:T.borderAccent,marginBottom:req.message?6:0}}>{req.email}</div>
                        {req.message&&(
                          <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic",
                            overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box" as any,
                            WebkitLineClamp:2,WebkitBoxOrient:"vertical" as any}}>
                            "{req.message}"
                          </div>
                        )}
                      </div>
                      {/* Action buttons — only on pending */}
                      {req.status==="pending"&&(
                        <div style={{display:"flex",gap:6,flexShrink:0}}>
                          <button
                            disabled={inFlight}
                            onClick={async()=>{
                              setAccessActionInFlight(req.email);
                              try{ await onApproveRequest?.(req.email); }
                              finally{ setAccessActionInFlight(null); }
                            }}
                            style={{padding:"5px 12px",borderRadius:6,border:"none",cursor:inFlight?"default":"pointer",fontFamily:FONT,
                              fontSize:11,fontWeight:600,background:T.borderAccent,color:"#0f1117",opacity:inFlight?.5:1,transition:"opacity .15s"}}>
                            {inFlight?"…":"Approve"}
                          </button>
                          <button
                            disabled={inFlight}
                            onClick={async()=>{
                              setAccessActionInFlight(req.email);
                              try{ await onRejectRequest?.(req.email); }
                              finally{ setAccessActionInFlight(null); }
                            }}
                            style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${T.borderSecondary}`,cursor:inFlight?"default":"pointer",fontFamily:FONT,
                              fontSize:11,fontWeight:400,background:"none",color:T.textMuted,opacity:inFlight?.5:1,transition:"opacity .15s"}}>
                            Reject
                          </button>
                        </div>
                      )}
                      {/* Status badge for resolved */}
                      {req.status==="approved"&&(
                        <span style={{fontSize:10,fontWeight:700,color:"#22c55e",background:"#22c55e15",borderRadius:6,padding:"3px 8px",flexShrink:0}}>Approved</span>
                      )}
                      {req.status==="rejected"&&(
                        <span style={{fontSize:10,fontWeight:700,color:T.textWeak,background:T.bgToast,borderRadius:6,padding:"3px 8px",flexShrink:0}}>Rejected</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    )}

    </div>{/* end content pane */}
  </div>
);
}
